import React, { useEffect, useState } from 'react';
import {
  Upload,
  CheckCircle2,
  Circle,
  AlertTriangle,
  Link2,
  Unlink,
  Lock,
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeft,
  Search,
  X,
  Sparkles
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import Modal from '../components/Modal';
import BotonAyuda from '../components/BotonAyuda';

function esperar(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// La conciliación es siempre por mes calendario (acordado con el usuario
// 2026-09-14) — de un input `type="month"` (YYYY-MM) se derivan el primer y
// el último día de ese mes.
function rangoDelMes(mes) {
  if (!mes) return null;
  const [anio, m] = mes.split('-').map(Number);
  const fecha_desde = `${mes}-01`;
  const ultimoDia = new Date(anio, m, 0).getDate();
  const fecha_hasta = `${mes}-${String(ultimoDia).padStart(2, '0')}`;
  return { fecha_desde, fecha_hasta };
}

// Igual que normalizarReferencia en el backend: solo letras/números en
// mayúscula, para comparar aunque el banco use otro espaciado/guiones.
function normalizarRef(s) {
  return String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Arma "descripción · ref: X", salvo que la descripción ya traiga esa
// referencia adentro (pasa con movimientos viejos, o con el texto que ya
// imprime el propio banco) — ahí se repetía.
function descripcionConRef(descripcion, referencia) {
  const desc = descripcion || '';
  if (!referencia) return desc;
  if (normalizarRef(desc).includes(normalizarRef(referencia))) return desc;
  return `${desc} · ref: ${referencia}`.trim();
}

function BadgeEstadoLinea({ estado }) {
  const estilos = {
    pendiente: 'bg-slate-100 text-slate-600',
    sugerida: 'bg-amber-100 text-amber-700',
    conciliada: 'bg-green-100 text-green-700'
  };
  const textos = { pendiente: 'Pendiente', sugerida: 'Sugerida', conciliada: 'Conciliada' };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${estilos[estado]}`}>{textos[estado]}</span>;
}

export default function Conciliacion() {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState('');
  const [historial, setHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const [detalle, setDetalle] = useState(null);
  const [errorDetalle, setErrorDetalle] = useState('');

  const [mostrarNueva, setMostrarNueva] = useState(false);
  const [mes, setMes] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [contadorAnalisis, setContadorAnalisis] = useState(0);
  const [errorNueva, setErrorNueva] = useState('');

  const [mostrarConciliadas, setMostrarConciliadas] = useState(false);
  const [lineaCandidatos, setLineaCandidatos] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [buscarCandidato, setBuscarCandidato] = useState('');

  // Sugerencias de parejas (reglas flexibles + IA). No se guardan: se piden
  // de nuevo cuando hace falta y cada una se confirma con el botón de siempre.
  const [sugerencias, setSugerencias] = useState(null);
  const [buscandoSugerencias, setBuscandoSugerencias] = useState(false);
  const [avisoSugerencias, setAvisoSugerencias] = useState('');
  const [usoSugerencias, setUsoSugerencias] = useState(null);

  useEffect(() => {
    if (empresaActiva) {
      api.get('/cuentas').then(({ data }) => setCuentas(data.filter((c) => c.tipo === 'banco')));
    }
  }, [empresaActiva]);

  async function cargarHistorial(idCuenta) {
    setCargandoHistorial(true);
    try {
      const { data } = await api.get(`/conciliacion/cuenta/${idCuenta}`);
      setHistorial(data);
    } finally {
      setCargandoHistorial(false);
    }
  }

  useEffect(() => {
    if (cuentaId) cargarHistorial(cuentaId);
    else setHistorial([]);
    setDetalle(null);
  }, [cuentaId]);

  async function abrirDetalle(id) {
    setErrorDetalle('');
    setMostrarConciliadas(false);
    if (id !== detalle?.id) {
      setSugerencias(null);
      setAvisoSugerencias('');
      setUsoSugerencias(null);
    }
    try {
      const { data } = await api.get(`/conciliacion/${id}`);
      setDetalle(data);
    } catch (err) {
      setErrorDetalle(err.response?.data?.error || 'No se pudo cargar la conciliación');
    }
  }

  function cerrarModalNueva() {
    setMostrarNueva(false);
    setMes('');
    setArchivo(null);
    setErrorNueva('');
    setContadorAnalisis(0);
  }

  async function analizarYCrear() {
    if (!archivo) return setErrorNueva('Elegí el archivo del estado de cuenta');
    const rango = rangoDelMes(mes);
    if (!rango) return setErrorNueva('Elegí el mes del estado de cuenta');

    setAnalizando(true);
    setErrorNueva('');
    setContadorAnalisis(0);
    try {
      const datosArchivo = new FormData();
      datosArchivo.append('archivo', archivo);
      const { data } = await api.post('/adjuntos/extraer-estado-cuenta/iniciar', datosArchivo);
      const jobId = data.job_id;

      let trabajo;
      do {
        await esperar(3000);
        ({ data: trabajo } = await api.get(`/adjuntos/extraer-estado-cuenta/estado/${jobId}`));
        setContadorAnalisis(trabajo.contador || 0);
      } while (trabajo.estado === 'procesando');

      if (trabajo.estado === 'error') {
        setErrorNueva(trabajo.error || 'No se pudo analizar el estado de cuenta');
        return;
      }
      if (!trabajo.movimientos || trabajo.movimientos.length === 0) {
        setErrorNueva('No se encontraron líneas en el archivo');
        return;
      }

      const { data: creada } = await api.post('/conciliacion', {
        cuenta_id: cuentaId,
        fecha_desde: rango.fecha_desde,
        fecha_hasta: rango.fecha_hasta,
        archivo_nombre: archivo.name,
        lineas: trabajo.movimientos
      });

      cerrarModalNueva();
      await cargarHistorial(cuentaId);
      await abrirDetalle(creada.id);
    } catch (err) {
      setErrorNueva(err.response?.data?.error || 'No se pudo procesar el estado de cuenta');
    } finally {
      setAnalizando(false);
    }
  }

  function verCandidatos(linea) {
    setLineaCandidatos(linea);
    setBuscarCandidato('');
  }

  // Al abrir el panel trae los candidatos naturales (monto/fecha cercanos)
  // de una; al escribir, filtra por texto en el servidor (debounce 300ms).
  useEffect(() => {
    if (!lineaCandidatos || !detalle) return;
    const t = setTimeout(() => {
      api.get(`/conciliacion/${detalle.id}/lineas/${lineaCandidatos.id}/candidatos`, {
        params: { q: buscarCandidato || undefined }
      }).then(({ data }) => setCandidatos(data));
    }, buscarCandidato ? 300 : 0);
    return () => clearTimeout(t);
  }, [lineaCandidatos, buscarCandidato, detalle]);

  async function confirmarVinculo(lineaId, movimientoId) {
    try {
      await api.post(`/conciliacion/${detalle.id}/lineas/${lineaId}/confirmar`, { movimiento_id: movimientoId });
      setLineaCandidatos(null);
      await abrirDetalle(detalle.id);
      await cargarHistorial(cuentaId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo confirmar el vínculo');
    }
  }

  async function pedirSugerencias() {
    setBuscandoSugerencias(true);
    setAvisoSugerencias('');
    try {
      const { data } = await api.post(`/conciliacion/${detalle.id}/sugerencias-ia`);
      let trabajo;
      do {
        await esperar(2000);
        ({ data: trabajo } = await api.get(`/conciliacion/sugerencias-ia/${data.job_id}`));
      } while (trabajo.estado === 'procesando');
      if (trabajo.estado === 'error') {
        setAvisoSugerencias(trabajo.error || 'No se pudieron calcular las sugerencias');
        return;
      }
      setSugerencias(trabajo.sugerencias);
      setUsoSugerencias(trabajo.uso);
      setAvisoSugerencias(trabajo.aviso || '');
    } catch (err) {
      setAvisoSugerencias(err.response?.data?.error || 'No se pudieron calcular las sugerencias');
    } finally {
      setBuscandoSugerencias(false);
    }
  }

  async function aceptarSugerencia(s) {
    try {
      await api.post(`/conciliacion/${detalle.id}/lineas/${s.linea_id}/confirmar`, { movimiento_id: s.movimiento_id, origen: s.origen });
      setSugerencias((prev) => prev.filter((x) => x.linea_id !== s.linea_id));
      await abrirDetalle(detalle.id);
      await cargarHistorial(cuentaId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo confirmar el vínculo');
    }
  }

  function descartarSugerencia(s) {
    setSugerencias((prev) => prev.filter((x) => x.linea_id !== s.linea_id));
  }

  async function desvincular(lineaId) {
    try {
      await api.delete(`/conciliacion/${detalle.id}/lineas/${lineaId}/vinculo`);
      await abrirDetalle(detalle.id);
      await cargarHistorial(cuentaId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo deshacer el vínculo');
    }
  }

  async function cerrarPeriodo() {
    if (!window.confirm('¿Cerrar este período? Ya no se podrán editar los vínculos salvo que se reabra.')) return;
    try {
      await api.post(`/conciliacion/${detalle.id}/cerrar`);
      await abrirDetalle(detalle.id);
      await cargarHistorial(cuentaId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cerrar el período');
    }
  }

  async function eliminarConciliacion() {
    if (!window.confirm('¿Eliminar esta conciliación? Los movimientos vinculados quedan sin conciliar.')) return;
    try {
      await api.delete(`/conciliacion/${detalle.id}`);
      setDetalle(null);
      await cargarHistorial(cuentaId);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la conciliación');
    }
  }

  return (
    <div className="space-y-6">
      {!detalle && (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold text-slate-800">
              Conciliación Bancaria
              {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
              <BotonAyuda />
            </h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[240px]">
                <label className="block text-xs text-slate-500 mb-1">Cuenta bancaria</label>
                <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} className="border rounded-md px-3 py-2 w-full">
                  <option value="">Seleccioná una cuenta...</option>
                  {cuentas.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre} ({c.moneda})</option>
                  ))}
                </select>
              </div>
              {cuentaId && (
                <button
                  onClick={() => setMostrarNueva(true)}
                  title="Nueva conciliación: cargar estado de cuenta"
                  className="p-2 rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors"
                >
                  <Upload size={18} />
                </button>
              )}
            </div>

            {cuentaId && (
              <div>
                {cargandoHistorial && <p className="text-sm text-slate-400">Cargando historial...</p>}
                {!cargandoHistorial && historial.length === 0 && (
                  <p className="text-sm text-slate-400">Esta cuenta todavía no tiene ninguna conciliación cargada.</p>
                )}
                {historial.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500 border-b">
                          <th className="py-2 pr-3">Período</th>
                          <th className="py-2 pr-3">Estado</th>
                          <th className="py-2 pr-3">Líneas banco</th>
                          <th className="py-2 pr-3">Conciliadas</th>
                          <th className="py-2 pr-3">Sin match sistema</th>
                          <th className="py-2 pr-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {historial.map((h) => {
                          const cien = h.estado === 'cerrada' || (Number(h.lineas_conciliadas) === Number(h.total_lineas) && Number(h.movimientos_sin_match) === 0);
                          return (
                            <tr key={h.id} className="border-b last:border-0 hover:bg-slate-50">
                              <td className="py-2 pr-3">{formatFecha(h.fecha_desde)} — {formatFecha(h.fecha_hasta)}</td>
                              <td className="py-2 pr-3">
                                {h.estado === 'cerrada' ? (
                                  <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 text-xs px-2 py-0.5 rounded-full">
                                    <Lock size={12} /> Cerrada 100%
                                  </span>
                                ) : cien ? (
                                  <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 text-xs px-2 py-0.5 rounded-full">
                                    <CheckCircle2 size={12} /> 100% lista para cerrar
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 text-xs px-2 py-0.5 rounded-full">
                                    <Circle size={12} /> En progreso
                                  </span>
                                )}
                              </td>
                              <td className="py-2 pr-3 tabular-nums">{h.total_lineas}</td>
                              <td className="py-2 pr-3 tabular-nums">{h.lineas_conciliadas}</td>
                              <td className="py-2 pr-3 tabular-nums">{h.movimientos_sin_match}</td>
                              <td className="py-2 pr-3 text-right">
                                <button onClick={() => abrirDetalle(h.id)} className="text-primary-600 hover:underline text-sm">
                                  Ver
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {mostrarNueva && (
        <Modal title="Nueva conciliación bancaria" onClose={analizando ? () => {} : cerrarModalNueva}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Mes a conciliar</label>
              <input
                type="month"
                value={mes}
                onChange={(e) => setMes(e.target.value)}
                className="border rounded-md px-3 py-2 w-full sm:w-56"
                disabled={analizando}
              />
              <p className="text-[11px] text-slate-400 mt-1">La conciliación siempre cubre el mes calendario completo.</p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Estado de cuenta oficial del banco (PDF o Excel)</label>
              <input
                type="file"
                accept=".pdf,.xls,.xlsx,image/*"
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                className="border rounded-md px-3 py-2 w-full text-sm"
                disabled={analizando}
              />
            </div>
            {analizando && (
              <p className="text-sm text-slate-500">Analizando el archivo con IA... {contadorAnalisis > 0 ? `${contadorAnalisis} líneas leídas` : ''}</p>
            )}
            {errorNueva && <p className="text-sm text-red-600">{errorNueva}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={cerrarModalNueva} disabled={analizando} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">
                Cancelar
              </button>
              <button
                onClick={analizarYCrear}
                disabled={analizando}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50"
              >
                {analizando ? 'Procesando...' : 'Analizar y conciliar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {errorDetalle && <p className="text-sm text-red-600">{errorDetalle}</p>}

      {detalle && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDetalle(null)}
              title="Volver al historial"
              className="text-slate-400 hover:text-slate-600"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-slate-800">
              Conciliación · {formatFecha(detalle.fecha_desde)} — {formatFecha(detalle.fecha_hasta)}
              {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
              <BotonAyuda />
            </h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-4 text-sm text-slate-600">
                <span>Líneas banco: <strong>{detalle.lineas.length}</strong></span>
                <span>Conciliadas: <strong className="text-green-700">{detalle.lineas.filter((l) => l.estado === 'conciliada').length}</strong></span>
                <span>Pendientes: <strong className="text-amber-700">{detalle.lineas.filter((l) => l.estado !== 'conciliada').length}</strong></span>
                <span>Sin match en sistema: <strong className="text-red-700">{detalle.movimientos_sin_match.length}</strong></span>
              </div>
              <div className="flex gap-2">
                {detalle.estado !== 'cerrada' && (
                  <button onClick={eliminarConciliacion} title="Eliminar conciliación" className="text-slate-400 hover:text-red-600">
                    <Trash2 size={18} />
                  </button>
                )}
                {detalle.estado === 'cerrada' ? (
                  <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 text-xs px-3 py-1.5 rounded-md">
                    <Lock size={14} /> Período cerrado
                  </span>
                ) : (
                  <button
                    onClick={cerrarPeriodo}
                    disabled={detalle.lineas.some((l) => l.estado !== 'conciliada') || detalle.movimientos_sin_match.length > 0}
                    title={
                      detalle.lineas.some((l) => l.estado !== 'conciliada') || detalle.movimientos_sin_match.length > 0
                        ? 'Todavía hay líneas o movimientos sin conciliar'
                        : 'Cerrar el período'
                    }
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Lock size={14} /> Cerrar período
                  </button>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-700">Líneas del estado de cuenta</h3>
                <div className="flex items-center gap-3">
                  {detalle.lineas.some((l) => l.estado === 'conciliada') && (
                    <button onClick={() => setMostrarConciliadas((v) => !v)} className="text-xs text-primary-600 hover:underline">
                      {mostrarConciliadas
                        ? 'Ocultar conciliadas'
                        : `Mostrar ${detalle.lineas.filter((l) => l.estado === 'conciliada').length} conciliada(s)`}
                    </button>
                  )}
                  {detalle.estado !== 'cerrada' && detalle.lineas.some((l) => l.estado === 'pendiente') && (
                    <button
                      onClick={pedirSugerencias}
                      disabled={buscandoSugerencias}
                      title="Busca parejas para las líneas pendientes: primero por reglas, después con IA. Nada se vincula sin tu clic."
                      className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-md text-sm disabled:opacity-50 flex items-center gap-1"
                    >
                      <Sparkles size={14} /> {buscandoSugerencias ? 'Buscando parejas...' : 'Sugerir parejas con IA'}
                    </button>
                  )}
                </div>
              </div>

              {(sugerencias || avisoSugerencias) && (
                <div className="mb-4 border border-primary-200 bg-primary-50/40 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
                      <Sparkles size={14} className="text-primary-600" />
                      {sugerencias && sugerencias.length > 0
                        ? `${sugerencias.length} pareja(s) sugerida(s): revisalas y aceptá o descartá cada una`
                        : sugerencias ? 'No se encontraron parejas para las líneas pendientes' : 'Sugerencias'}
                      {usoSugerencias && <span className="text-xs text-slate-400 font-normal">{` · ≈ $${formatMonto(usoSugerencias.costoUsd, 4)}`}</span>}
                    </p>
                    <button
                      onClick={() => { setSugerencias(null); setAvisoSugerencias(''); }}
                      title="Cerrar sugerencias"
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {avisoSugerencias && (
                    <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle size={12} /> {avisoSugerencias}</p>
                  )}
                  {sugerencias && sugerencias.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-slate-500 border-b">
                            <th className="py-2 pr-3">Línea del banco</th>
                            <th className="py-2 pr-3">Movimiento del sistema</th>
                            <th className="py-2 pr-3">Por qué</th>
                            <th className="py-2 pr-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sugerencias.map((s) => (
                            <tr key={s.linea_id} className="border-b last:border-0 align-top">
                              <td className="py-2 pr-3">
                                <div className="tabular-nums">{formatFecha(s.linea?.fecha)} · <strong>{formatMonto(s.linea?.monto)}</strong></div>
                                <div className="text-xs text-slate-500">{descripcionConRef(s.linea?.descripcion, s.linea?.referencia)}</div>
                              </td>
                              <td className="py-2 pr-3">
                                <div className="tabular-nums">{formatFecha(s.movimiento?.fecha)} · <strong>{formatMonto(s.movimiento?.monto)}</strong></div>
                                <div className="text-xs text-slate-500">
                                  {descripcionConRef(s.movimiento?.descripcion, s.movimiento?.referencia)}
                                  {s.movimiento?.nombre_receptor ? ` · ${s.movimiento.nombre_receptor}` : ''}
                                </div>
                              </td>
                              <td className="py-2 pr-3 text-xs">
                                <span className={`px-1.5 py-0.5 rounded-full mr-1 ${s.origen === 'ia' ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {s.origen === 'ia' ? (s.confianza === 'alta' ? 'IA · alta' : 'IA · revisar') : 'Regla'}
                                </span>
                                {s.motivo}
                              </td>
                              <td className="py-2 pr-3 whitespace-nowrap text-right">
                                <button onClick={() => aceptarSugerencia(s)} title="Aceptar y conciliar" className="text-green-600 hover:text-green-800 mr-2">
                                  <CheckCircle2 size={18} />
                                </button>
                                <button onClick={() => descartarSugerencia(s)} title="Descartar sugerencia" className="text-slate-400 hover:text-red-600">
                                  <X size={18} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-3">Fecha</th>
                      <th className="py-2 pr-3">Tipo</th>
                      <th className="py-2 pr-3 text-right">Monto</th>
                      <th className="py-2 pr-3">Descripción / Ref.</th>
                      <th className="py-2 pr-3">Movimiento vinculado</th>
                      <th className="py-2 pr-3">Estado</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.lineas
                      .filter((l) => mostrarConciliadas || l.estado !== 'conciliada')
                      .map((l) => {
                      const abierta = lineaCandidatos?.id === l.id;
                      return (
                      <React.Fragment key={l.id}>
                      <tr className={`border-b last:border-0 ${abierta ? 'bg-primary-50/40' : ''}`}>
                        <td className="py-2 pr-3">{formatFecha(l.fecha)}</td>
                        <td className="py-2 pr-3">
                          {l.tipo === 'ingreso' ? (
                            <ArrowUpCircle size={16} className="text-green-600" />
                          ) : (
                            <ArrowDownCircle size={16} className="text-red-600" />
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{formatMonto(l.monto)}</td>
                        <td className="py-2 pr-3 text-slate-500 text-xs">
                          {descripcionConRef(l.descripcion, l.referencia)}
                        </td>
                        <td className="py-2 pr-3 text-xs text-slate-500">
                          {l.movimiento_id ? descripcionConRef(l.mov_descripcion, l.mov_referencia) : '—'}
                        </td>
                        <td className="py-2 pr-3"><BadgeEstadoLinea estado={l.estado} /></td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          {detalle.estado === 'cerrada' ? null : l.estado === 'conciliada' ? (
                            <button onClick={() => desvincular(l.id)} title="Deshacer vínculo" className="text-slate-400 hover:text-red-600">
                              <Unlink size={16} />
                            </button>
                          ) : l.estado === 'sugerida' ? (
                            <span className="inline-flex gap-2">
                              <button onClick={() => confirmarVinculo(l.id, l.movimiento_id)} title="Confirmar vínculo sugerido" className="text-green-600 hover:text-green-700">
                                <CheckCircle2 size={16} />
                              </button>
                              <button onClick={() => desvincular(l.id)} title="Quitar vínculo sugerido" className="text-slate-400 hover:text-red-600">
                                <Unlink size={16} />
                              </button>
                              <button
                                onClick={() => (abierta ? setLineaCandidatos(null) : verCandidatos(l))}
                                title="Buscar otro movimiento"
                                className={abierta ? 'text-primary-600' : 'text-slate-400 hover:text-primary-600'}
                              >
                                <Search size={16} />
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => (abierta ? setLineaCandidatos(null) : verCandidatos(l))}
                              title="Vincular manualmente"
                              className={abierta ? 'text-primary-600' : 'text-slate-400 hover:text-primary-600'}
                            >
                              <Link2 size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                      {abierta && (
                        <tr className="bg-primary-50/40 border-b last:border-0">
                          <td colSpan={7} className="p-3">
                            <div className="bg-white border rounded-md p-3">
                              <div className="flex items-center justify-between mb-2 gap-3">
                                <p className="text-xs text-slate-500">
                                  Elegí el movimiento del sistema que corresponde a esta línea del banco.
                                </p>
                                <button onClick={() => setLineaCandidatos(null)} className="text-slate-400 hover:text-slate-600 shrink-0">
                                  <X size={16} />
                                </button>
                              </div>
                              <input
                                autoFocus
                                value={buscarCandidato}
                                onChange={(e) => setBuscarCandidato(e.target.value)}
                                placeholder="Buscar por descripción, referencia o receptor..."
                                className="border rounded-md px-3 py-1.5 text-sm w-full mb-2"
                              />
                              {candidatos.length === 0 && (
                                <p className="text-sm text-slate-400">
                                  {buscarCandidato ? 'Ningún movimiento coincide con la búsqueda.' : 'No hay movimientos candidatos en esta cuenta.'}
                                </p>
                              )}
                              <div className="space-y-1 max-h-72 overflow-y-auto">
                                {candidatos.map((m) => {
                                  const sugerido = Math.abs(Number(m.monto) - Number(l.monto)) < 0.5;
                                  return (
                                    <button
                                      key={m.id}
                                      onClick={() => confirmarVinculo(l.id, m.id)}
                                      className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-md hover:bg-slate-50 border text-sm ${sugerido ? 'bg-primary-50' : ''}`}
                                    >
                                      <span>
                                        {sugerido && (
                                          <span className="mr-1 text-[10px] font-semibold text-white bg-primary-600 rounded px-1 py-0.5 align-middle">
                                            Sugerido
                                          </span>
                                        )}
                                        {formatFecha(m.fecha)} · {m.descripcion} {m.nombre_receptor ? `· ${m.nombre_receptor}` : ''}
                                      </span>
                                      <span className="tabular-nums font-medium">{formatMonto(m.monto)}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                    {!mostrarConciliadas && detalle.lineas.every((l) => l.estado === 'conciliada') && (
                      <tr>
                        <td colSpan={7} className="py-4 text-center text-sm text-slate-400">
                          Todas las líneas están conciliadas.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {detalle.movimientos_sin_match.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-red-600" />
                  Movimientos del sistema que no están en el estado de cuenta del banco
                </h3>
                <p className="text-xs text-slate-500 mb-2">
                  Revisá si son cargas falsas, duplicadas, o si aún faltan por aparecer en el banco.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-3">Fecha</th>
                        <th className="py-2 pr-3">Tipo</th>
                        <th className="py-2 pr-3 text-right">Monto</th>
                        <th className="py-2 pr-3">Descripción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.movimientos_sin_match.map((m) => (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-2 pr-3">{formatFecha(m.fecha)}</td>
                          <td className="py-2 pr-3">
                            {m.tipo === 'ingreso' ? (
                              <ArrowUpCircle size={16} className="text-green-600" />
                            ) : (
                              <ArrowDownCircle size={16} className="text-red-600" />
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">{formatMonto(m.monto)}</td>
                          <td className="py-2 pr-3 text-slate-500 text-xs">{m.descripcion} {m.nombre_receptor ? `· ${m.nombre_receptor}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {detalle.sin_pago_factura.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-amber-600" />
                  Movimientos sin pago de factura vinculado
                </h3>
                <p className="text-xs text-slate-500 mb-2">
                  Informativo — no bloquea el cierre del período. Vinculá el pago desde el estado de cuenta del cliente o proveedor correspondiente.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-3">Fecha</th>
                        <th className="py-2 pr-3 text-right">Monto</th>
                        <th className="py-2 pr-3">Categoría</th>
                        <th className="py-2 pr-3">Descripción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.sin_pago_factura.map((m) => (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-2 pr-3">{formatFecha(m.fecha)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{formatMonto(m.monto)}</td>
                          <td className="py-2 pr-3 text-slate-500 text-xs">{m.categoria_nombre}</td>
                          <td className="py-2 pr-3 text-slate-500 text-xs">{m.descripcion} {m.nombre_receptor ? `· ${m.nombre_receptor}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
